// ============================================================
// SolicitudDetallePage
// ============================================================
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card, Descriptions, Tag, Typography, Space, Button,
  message, Skeleton, Alert, Select, Form, Input, Divider,
} from "antd";
import { ArrowLeftOutlined, EditOutlined, SaveOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { solicitudesApi } from "../api/client";
import type { SolicitudArea, EstadoSolicitudEnum } from "../types";

const { Title, Text } = Typography;
const { Option } = Select;

const ESTADO_COLORS: Record<string, string> = {
  Nueva: "blue", "En revisión": "orange", "Aprobada→Proyecto": "green",
  "En desarrollo IABP": "purple", Escalada: "red", Descartada: "default", "En espera": "warning",
};

export function SolicitudDetallePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [sol, setSol] = useState<SolicitudArea | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const cargar = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await solicitudesApi.obtener(id);
      setSol(res.data);
      form.setFieldsValue(res.data);
    } catch {
      message.error("Solicitud no encontrada");
      navigate("/solicitudes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, [id]);

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      await solicitudesApi.actualizar(id!, values);
      message.success("Solicitud actualizada");
      setEditing(false);
      cargar();
    } catch { message.error("Error al actualizar"); }
    finally { setSaving(false); }
  };

  const handleCrearProyecto = async () => {
    try {
      const res = await solicitudesApi.crearProyecto(id!);
      message.success(`Proyecto ${res.data.proj_id} creado`);
      navigate(`/proyectos/${res.data.id}`);
    } catch (err: any) {
      message.error(err.response?.data?.detail ?? "Error");
    }
  };

  if (loading || !sol) return <Skeleton active paragraph={{ rows: 8 }} />;

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} type="text" onClick={() => navigate("/solicitudes")} />
        <Text code style={{ fontSize: 13 }}>{sol.sol_id}</Text>
        <Tag color={ESTADO_COLORS[sol.estado]}>{sol.estado}</Tag>
        {sol.sugerir_quick_win && <Tag color="orange" icon={<ThunderboltOutlined />}>Candidata QW</Tag>}
      </Space>

      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
        <Title level={4} style={{ margin: 0, color: "#1a1f5e" }}>
          {sol.area_solicitante} — {sol.tipo_solicitud}
        </Title>
        <Space>
          {sol.puede_crear_proyecto && (
            <Button type="primary" icon={<ThunderboltOutlined />}
              onClick={handleCrearProyecto} style={{ background: "#52c41a", borderColor: "#52c41a" }}>
              Crear proyecto
            </Button>
          )}
          {!editing ? (
            <Button icon={<EditOutlined />} onClick={() => setEditing(true)}>Editar</Button>
          ) : (
            <>
              <Button onClick={() => { setEditing(false); form.setFieldsValue(sol); }}>Cancelar</Button>
              <Button type="primary" icon={<SaveOutlined />} loading={saving}
                onClick={handleSave} style={{ background: "#1a1f5e" }}>Guardar</Button>
            </>
          )}
        </Space>
      </div>

      {!editing ? (
        <Card size="small">
          <Descriptions column={{ xs: 1, sm: 2 }} size="small">
            <Descriptions.Item label="Solicitante">{sol.solicitante_nombre}</Descriptions.Item>
            <Descriptions.Item label="Área">{sol.area_solicitante}</Descriptions.Item>
            <Descriptions.Item label="Tipo">{sol.tipo_solicitud}</Descriptions.Item>
            <Descriptions.Item label="Prioridad">
              <Tag color={{ Urgente: "red", Alta: "orange", Media: "blue", Baja: "default" }[sol.prioridad]}>{sol.prioridad}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Complejidad">{sol.complejidad}</Descriptions.Item>
            <Descriptions.Item label="Esfuerzo estimado">{sol.esfuerzo_estimado_iabp}</Descriptions.Item>
            <Descriptions.Item label="Impacto esperado">{sol.impacto_esperado}</Descriptions.Item>
            <Descriptions.Item label="Triage CAI">{sol.requiere_triage_cai ? "Sí" : "No"}</Descriptions.Item>
            <Descriptions.Item label="Descripción" span={2}>{sol.descripcion_pain_point}</Descriptions.Item>
            {sol.siguiente_paso && (
              <Descriptions.Item label="Siguiente paso" span={2}>{sol.siguiente_paso}</Descriptions.Item>
            )}
          </Descriptions>
        </Card>
      ) : (
        <Card size="small">
          <Form form={form} layout="vertical">
            <Form.Item name="estado" label="Estado">
              <Select>
                {["Nueva","En revisión","Aprobada→Proyecto","En desarrollo IABP","Escalada","Descartada","En espera"]
                  .map((e) => <Option key={e} value={e}>{e}</Option>)}
              </Select>
            </Form.Item>
            <Form.Item name="siguiente_paso" label="Siguiente paso">
              <Input.TextArea rows={2} />
            </Form.Item>
            <Form.Item name="comentarios" label="Comentarios">
              <Input.TextArea rows={3} />
            </Form.Item>
          </Form>
        </Card>
      )}
    </div>
  );
}

export default SolicitudDetallePage;
